import React from "react";
import { Box, IconButton, Theme, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useDraggable, useDroppable } from "@dnd-kit/react";

import { Kpi } from "../Alert/Filter/FilterType";
import { Column, Row } from "../Layout/utils";
import { WithTooltip } from "./DragDropPerType";
import { SCROLL_BAR_STYLES_THIN } from "../Theme";

type DragData = {
  column: "source" | "target";
  itemId?: number;
  groupId?: number;
  index?: number;
};

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    backgroundColor: theme.vars.palette.background.default,
    overflowY: "auto",
    maxHeight: "300px",
    ...SCROLL_BAR_STYLES_THIN,
  },
  deletIcon: {
    padding: "3px",
  },
}));

const DropZoneRow = ({
  itemId,
  index,
  children,
}: {
  itemId: number;
  index: number;
  children: React.ReactNode;
}) => {
  const { ref } = useDroppable({
    id: `target-drop-${itemId}`,
    data: { column: "target", index } as DragData,
  });
  return <div ref={ref as React.RefCallback<HTMLDivElement>}>{children}</div>;
};

const DraggableTargetItem = ({
  item,
  index,
  icon,
  buttonClick,
}: {
  item: Kpi;
  index: number;
  icon: JSX.Element;
  buttonClick: (itemId: number) => void;
}) => {
  const { classes } = useStyles();

  const { ref, isDragging } = useDraggable({
    id: `target-drag-${item.id}`,
    data: { column: "target", itemId: item.id, index } as DragData,
  });

  return (
    <Box
      ref={ref as React.RefCallback<HTMLDivElement>}
      sx={{
        userSelect: "none",
        opacity: isDragging ? 0.4 : 1,
        backgroundColor: "background.paper",
        margin: "5px",
        display: "flex",
        flexDirection: "row",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        cursor: "grab",
        transition: "background-color 0.15s",
      }}
    >
      <WithTooltip
        title={item.label}
        style={{
          flex: "1 1 auto",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            flexGrow: 1,
            pl: 2,
          }}
        >
          <Typography
            component={"span"}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {item.label}
          </Typography>
        </Box>
      </WithTooltip>
      <IconButton
        className={classes.deletIcon}
        onClick={() => buttonClick(item.id)}
      >
        {icon}
      </IconButton>
    </Box>
  );
};

const EndDropZone = ({ index }: { index: number }) => {
  const { ref } = useDroppable({
    id: "target-drop-end",
    data: { column: "target", index } as DragData,
  });
  return (
    <div
      ref={ref as React.RefCallback<HTMLDivElement>}
      style={{ height: "8px" }}
    />
  );
};

export const DragDrop = ({
  list,
  title,
  columnId,
  icon,
  buttonClick,
}: {
  list: Kpi[];
  title: string;
  columnId: string;
  icon: JSX.Element;
  buttonClick: (itemId: number) => void;
}) => {
  const { classes } = useStyles();

  return (
    <Column
      sx={{
        width: "0px",
      }}
    >
      <Row
        sx={{
          alignItems: "center",
          justifyContent: "center",
          height: "48px",
          color: "text.secondary",
        }}
      >
        <Typography component={"span"}>{title}</Typography>
      </Row>
      <div className={classes.root}>
        <div style={{ flexGrow: 1 }}>
          {list.map((item, index) => (
            <DropZoneRow key={item.id} itemId={item.id} index={index}>
              <DraggableTargetItem
                item={item}
                index={index}
                icon={icon}
                buttonClick={buttonClick}
              />
            </DropZoneRow>
          ))}
          <EndDropZone index={list.length} />
        </div>
      </div>
    </Column>
  );
};
